import PropTypes from "prop-types";

import { motion } from "framer-motion";

import { getResultTableDependsParams } from "./getResultTableDependsParams";
import { nameInitials } from "./scorecardItem/nameInitials";
import { matchValues } from "./scorecardItem/matchValues";
import { Separator } from "./scorecardItem/Separator";
import { addSign } from "./scorecardItem/addSign";
import { opposite } from "./opposite";

/* eslint-disable react/prop-types */

function ResultTeamTotal({ teamName, matchType, ballCount, value }) {
  const { grosNetStringTeamTotal, showBestBallTitle, BallValue } =
    matchValues();

  return (
    <div
      style={{
        height: 48,
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        backgroundColor: "#EFEFEF",
      }}
    >
      {value ? (
        <div className="scorecardItem--result-pay-total ">{value}</div>
      ) : (
        <div
          style={{
            textTransform: "uppercase",
            fontFamily: "SF Pro Text",
            fontWeight: 700,
            fontSize: 12,
            color: "rgba(30, 38, 46, 0.6)",
            paddingLeft: 40,
            alignItems: "flex-start",
          }}
        >
          {teamName} {grosNetStringTeamTotal[matchType]}
          {showBestBallTitle[matchType] && (
            <div
              style={{
                textTransform: "lowercase",
                fontFamily: "SF Pro Text",
                fontWeight: 300,
                fontSize: 9,
                // lineHeight: 18,
                color: "rgba(30, 38, 46, 0.6)",
              }}
            >
              with {BallValue[ballCount]}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function TotalBar({
  currentTeam,
  usersList,
  showName,
  property,
  matchType,
  ballCount,
}) {
  const teamList = usersList.filter((team) => team.teamName === currentTeam);

  const reduce = teamList.reduce((acc, obj) => acc + obj[property], 0) || null;

  return (
    <div>
      {showName ? (
        <ResultTeamTotal
          teamName={currentTeam}
          matchType={matchType}
          ballCount={ballCount}
        />
      ) : (
        <ResultTeamTotal value={reduce} />
      )}
    </div>
  );
}

export default function ResultsTable({
  secondBetMatchType,
  matchType,
  usersList: ul,
  teamsPlay,
  onUserSelected,
  individuals,
  numberOfTeeTimes,
  ballCount,
}) {
  const groupedData = ul.reduce((acc, curr) => {
    (acc[curr.teamName] = acc[curr.teamName] || []).push(curr);
    return acc;
  }, {});

  const matrixScore = (users, index, gross) =>
    groupedData[users[index]?.teamName]?.map((uu) =>
      uu.holes.map((hole) => (gross ? hole.score : hole.netScore)),
    );

  const smallestNumber = (r, a) => r.map((b, i) => Math.min(a[i], b) || null);
  const summaryNumber = (r, a) => r.map((b, i) => a[i] + b || null);

  const calculateTeamQuota = (users, index) => {
    const allUserQuota = groupedData[users[index]?.teamName].reduce(
      (accumulator, currentUser) => accumulator + currentUser.quota,
      0,
    );

    const userQuota = groupedData[users[index]?.teamName]
      ?.map((uu) => uu.holes.map((hole) => hole.quota))
      .reduce(summaryNumber);

    const userQuotaSummary =
      userQuota.reduce((acc, value) => acc + value, 0) - allUserQuota;

    return userQuotaSummary;
  };

  const usersList = ul.map((user, index) => ({
    ...user,
    totalNetScore: user.holes.reduce((acc, hole) => acc + hole.netScore, 0),
    totalGrossScore: user.holes.reduce((acc, hole) => acc + hole.score, 0),

    teamNetSmallerNumberTotal:
      matrixScore(ul, index, false)
        .reduce(smallestNumber)
        .reduce((a, b) => a + b, 0) ?? 0,
    teamGrossSmallerNumberTotal:
      matrixScore(ul, index, true)
        .reduce(smallestNumber)
        .reduce((a, b) => a + b, 0) ?? 0,

    teamNetSumNumberTotal:
      matrixScore(ul, index, false)
        .reduce(summaryNumber)
        .reduce((a, b) => a + b, 0) ?? 0,

    teamGrossSumNumberTotal:
      matrixScore(ul, index, true)
        .reduce(summaryNumber)
        .reduce((a, b) => a + b, 0) ?? 0,

    teamQuotaTotal: addSign(calculateTeamQuota(ul, index)),
  }));

  const resultTabTableParams = getResultTableDependsParams(
    matchType,
    secondBetMatchType,
    teamsPlay,
    numberOfTeeTimes,
    ballCount,
  );

  const findIndexFirstLooser = usersList
    .sort((a, b) => b.net - a.net)
    .findIndex((i) => i.net <= 0);

  return (
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        border: 0,
        emptyCells: 0,
        backgroundColor: "#f2f9ff",
      }}
    >
      <thead>
        <tr>
          {resultTabTableParams()?.map((i) => (
            <th key={i.property} className="scorecard-results--header-text">
              {i.title}
            </th>
          ))}
        </tr>
      </thead>
      <motion.tbody layout>
        {usersList
          .sort((a, b) => b.net - a.net)
          .map((user, index) => {
            // eslint-disable-next-line prefer-destructuring
            const currentTeam = user.teamName;
            const previousTeam =
              index > 0 ? usersList[index - 1].teamName : null;

            const isTieMatch = usersList?.every((obj) => obj.net === 0);

            return (
              <tr key={user.uid} className="scorecard-results--item">
                {resultTabTableParams()?.map((i, idx) => {
                  const property = () => {
                    if (
                      i.title === "$" ||
                      i.title === "Ball 1" ||
                      i.title === "Ball 2" ||
                      i.title === "Ways" ||
                      i.title === "Quota total"
                    ) {
                      return opposite(user[i.property]);
                    }

                    return user[i.property] ?? "-";
                  };

                  return (
                    <td
                      key={i.property}
                      style={{
                        textAlign: idx === 0 ? "left" : "center",
                        padding: 0,
                      }}
                      onClick={() => onUserSelected(user.phone)}
                      aria-hidden="true"
                    >
                      {teamsPlay && currentTeam !== previousTeam && (
                        <Separator
                          showTime={idx === 0}
                          teeTimeString={currentTeam}
                        />
                      )}
                      {!isTieMatch && individuals && (
                        <Separator
                          showTime={idx === 0}
                          teeTimeString="Individuals"
                        />
                      )}
                      {!isTieMatch &&
                        !individuals &&
                        !teamsPlay &&
                        index === 0 && (
                          <Separator
                            showTime={idx === 0}
                            teeTimeString="Winners"
                          />
                        )}
                      {!isTieMatch &&
                        !individuals &&
                        !teamsPlay &&
                        index === findIndexFirstLooser && (
                          <Separator
                            showTime={idx === 0}
                            teeTimeString="Losers"
                          />
                        )}

                      {isTieMatch && index === 0 && (
                        <Separator
                          showTime={idx === 0}
                          teeTimeString="Match is a draw"
                        />
                      )}

                      {idx === 0 ? (
                        <div
                          style={{
                            flexDirection: "row",
                            display: "flex",
                            alignItems: "center",
                            minHeight: 50,
                          }}
                        >
                          <div
                            style={{ padding: 15 }}
                            className="scorecard-results--index"
                          >
                            {teamsPlay ? usersList[index].place : index + 1}
                          </div>
                          <div>
                            <div className="scorecard-results--name">
                              {nameInitials(user[i.property])}
                            </div>
                            <div className="scorecard-results--hdcp">
                              HDCP {user.courseHandicap}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="scorecardItem--result"
                          style={{
                            height: 50,
                            justifyContent: "center",
                            alignItems: "center",
                            display: "flex",

                            color: "#1E262E",
                          }}
                        >
                          {property()}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
      </motion.tbody>
    </table>
  );
}

ResultsTable.propTypes = {
  matchType: PropTypes.number.isRequired,
  onUserSelected: PropTypes.func.isRequired,
  secondBetMatchType: PropTypes.number.isRequired,
  teamsPlay: PropTypes.bool,
  usersList: PropTypes.oneOfType([
    PropTypes.shape({
      map: PropTypes.func,
      sort: PropTypes.func,
    }),
    PropTypes.any,
  ]).isRequired,
  numberOfTeeTimes: PropTypes.number.isRequired,
};

ResultsTable.defaultProps = {
  teamsPlay: false,
};
